import { createContext, useContext, ReactNode } from 'react';
import { DeliveryItem } from '../../types';

export interface DeliveryContextValue<T extends DeliveryItem, P> {
    isOpen: boolean;
    validItems: T[];
    invalidCount: number;
    selectedIds: number[];
    generatedResult: string | null;
    isProcessing: boolean;
    suggestedEmails: string[];
    setIsOpen: (open: boolean) => void;
    toggleSelection: (id: number) => void;
    executeAction: (payload: P) => void;
}

const DeliveryContext = createContext<unknown>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useDeliveryContext = <
    T extends DeliveryItem,
    P,
>(): DeliveryContextValue<T, P> => {
    const context = useContext(DeliveryContext) as
        | DeliveryContextValue<T, P>
        | undefined;

    if (!context) {
        throw new Error(
            'DeliveryModal components must be rendered within a <DeliveryModal.Root>',
        );
    }

    return context;
};

export interface DeliveryRootProps<T extends DeliveryItem, P> {
    children: ReactNode;
    value: DeliveryContextValue<T, P>;
}

export const DeliveryRoot = <T extends DeliveryItem, P>({
    children,
    value,
}: DeliveryRootProps<T, P>) => {
    return (
        <DeliveryContext.Provider value={value}>
            {children}
        </DeliveryContext.Provider>
    );
};
